/**
 * IReportPageParams: Tham số đầu vào cho API báo cáo theo trang
 */
export interface IReportPageParams {
  pageIds: number[];
  startTime: number;
  endTime: number;
  businessId: number;
  isGroupByHours?: number; // 1: Nhóm theo giờ, 0: Nhóm theo ngày
}

export interface IMetaDatasetItem {
  id: string;
  name: string;
  pageId: string;
  createdAt?: number;
}

/**
 * IReportPageItem: Cấu trúc dữ liệu của một bản ghi báo cáo
 */
export interface IReportPageItem {
  key?: string; // Chứa giờ nếu nhóm theo giờ (0, 1, 2...)
  date_string?: string; // Chứa ngày định dạng YYYYMMDD
  newCustomer: number; // Tổng khách mới
  newCustomerInbox: number; // Khách mới từ tin nhắn
  newCustomerComment: number; // Khách mới từ bình luận
  oldCustomersReMsg: number; // Khách cũ tương tác lại
  totalPhones: number; // Tổng số điện thoại
  newPhones: number; // Số điện thoại mới
  order: number; // Số đơn hàng
  orderAmount: number; // Doanh thu

  // Các trường extra do FE tính toán thêm hoặc dùng để hiển thị
  id?: string | number;
  total?: boolean; // Đánh dấu dòng tổng
  displayName?: string; // Hiển thị tên (VD: 'Tổng')
  fwbold?: boolean; // In đậm dòng
  day?: string; // Chuỗi ngày hiển thị (VD: '01/04')
  tbOrder?: number; // Doanh thu trung bình/đơn
  ratioOrderNewCustomer?: number; // Tỷ lệ đơn/khách mới
  ratioOrderPhone?: number; // Tỷ lệ đơn/SĐT
  ratioPhoneNewCustomer?: number; // Tỷ lệ SĐT/khách mới
}

/**
 * IReportInteractParams: Tham số đầu vào cho API báo cáo tương tác
 */
export interface IReportInteractParams {
  pageIds: string[];
  startTime: number;
  endTime: number;
  groupByPage: number; // 1: Nhóm theo page, 0: Nhóm theo ngày (mặc định)
}

/**
 * IReportInteractiveItem: Cấu trúc dữ liệu của một bản ghi báo cáo
 */
export interface IReportInteractiveItem {
  key?: string | number; // Theo ngày = timestamp, theo page = pageId
  date_string?: string; // Ngày dạng string (chỉ có khi groupByPage = 0)
  customer: number; // Tổng số hội thoại khách hàng
  new_customer: number; // Số hội thoại khách mới
  old_customer: number; // Số hội thoại khách cũ
  record: number; // Số record theo ngày
  phones: number; // Số hội thoại có SĐT
  inbox: number; // Số hội thoại inbox
  comment: number; // Số hội thoại comment
  order: number; // Số đơn hàng
  orderAmount: number; // Doanh thu đơn hàng (VNĐ)
  contentKey: string; // Giá trị hiển thị trên UI: ngày (`dd/MM/yyyy`) hoặc pageId
}

/**
 * IReportStaffParams: Tham số đầu vào cho API báo cáo theo nhân viên
 */
export interface IReportStaffParams {
  pageIds: string[];
  startTime: number;
  endTime: number;
  sort?: string;
  dir?: 'asc' | 'desc';
  isCareNewCustomer?: 0 | 1;
}

export const ReportStaffSplitConversationMode = {
  GROUP: 1,
  STAFF: 2,
} as const;

export type IReportStaffSplitConversationMode = typeof ReportStaffSplitConversationMode[keyof typeof ReportStaffSplitConversationMode];

export interface IReportStaffSplitConversationParams {
  pageIds: string[];
  startTime: number;
  endTime: number;
  mode: IReportStaffSplitConversationMode; // 1: Theo nhom, 2: Theo nhan vien
}

/**
 * IReportStaffItem: Cấu trúc dữ liệu của một nhân viên trong báo cáo
 */
export interface IReportStaffItem {
  id?: string;
  name?: string;
  interactInbox: number;
  interactComment: number;
  totalConversation: number;
  customerInbox: number;
  customerComment: number;
  newCustomer: number;
  oldCustomer: number;
  totalCustomer: number;
  totalOrder: number;
  amount: number;
  orderNewCustomer: number;
  orderOldCustomer: number;
  phoneByAssignee: number;
  careNewCustomer: number;
}

/**
 * IReportStaffRow: Dữ liệu đã format để render table/chart trên FE
 */
export interface IReportStaffRow extends IReportStaffItem {
  [key: string]: string | number | boolean | undefined;

  id?: string;
  name: string;
  total?: boolean;
  fwbold?: boolean;
}

export interface IReportStaffSplitConversationItem {
  id?: string;
  displayName?: string;
  assigneedSplit: number;
  assigneed: number;
  processed: number;
  processedSplit: number;
  order: number;
  transferred: number;
  transferredSplit: number;
}

export interface IReportStaffSplitConversationRow extends IReportStaffSplitConversationItem {
  [key: string]: string | number | boolean | undefined;

  name: string;
  handledRate?: string;
  closeRate?: string;
  total?: boolean;
  fwbold?: boolean;
}

/**
 * IReportTagParams: Tham số đầu vào cho API báo cáo theo nhãn
 **/
export interface IReportTagParams {
  pageIds: string[];
  startTime: number;
  endTime: number;
  groupIds?: string[]; // Thêm trường groupIds để lọc theo nhóm nhãn
  isMergeTag: 1 | 0; // 1: Gộp tag trùng tên, 0: Không gộp (Mặc định)
}

export interface IReportTagResponse {
  result: IReportTagItem[];
  totalTag: number;
  totalClickTag: number;
}

/**
 * IReportTagItem: Cấu trúc dữ liệu của một bản ghi báo cáo theo nhãn
 **/
export interface IReportTagItem {
  tag: IReportTag; // Thông tin nhãn
  days: number[]; // Mảng số ngày có tương tác với nhãn này
  total: number; // Tổng số lần gắn nhãm
}

export interface IReportTag {
  newTagId?: string[];
  backgroundColor: string;
  shortcut?: string | null;
  code: string;
  fanpageId: string;
  color: string;
  name: string;
  createdTime: number | string;
  status: number;
  order: number;
  groupId?: string;
  businessId?: string;
  typeBlock?: number;
}

export const ConversationTagReportViewType = {
  TAG: 'tag',
  HISTORY: 'history',
} as const;

export type IConversationTagReportViewType = typeof ConversationTagReportViewType[keyof typeof ConversationTagReportViewType];

/** Tham số API báo cáo nhãn hiện tại và lịch sử gắn/gỡ nhãn. */
export interface IConversationTagReportParams {
  pageIds?: string[];
  startTime: number;
  endTime: number;
  groupIds?: string[];
  viewType: IConversationTagReportViewType;
  isMergeTag?: 0 | 1;
  createdStartTime?: number;
  createdEndTime?: number;
}

/** Dữ liệu tổng hợp của báo cáo nhãn hội thoại theo từng chế độ xem. */
export interface IConversationTagReportResponse {
  result: IConversationTagReportItem[];
  totalTag: number;
  totalClickTag?: number;
  totalAdd?: number;
  totalRemove?: number;
}

/** Một dòng báo cáo nhãn hội thoại trả về từ API usagecurrent. */
export interface IConversationTagReportItem extends IReportTagItem {
  totalAdd?: number;
  totalRemove?: number;
}

/** Dòng dữ liệu đã chuyển đổi để hiển thị các cột ngày động trên DataGrid. */
export interface IConversationTagReportRow extends IConversationTagReportItem {
  detail: string;
  rowKey: string;
  [key: string]: string | number | number[] | IReportTag | undefined;
}

export const ReportPostType = {
  POST: 1,
  ADS: 2,
} as const;

export type IReportPostType = typeof ReportPostType[keyof typeof ReportPostType]; // 1: Theo bai viet, 2: Theo quang cao

/**
 * IReportPostParams: Tham so dau vao cho API bao cao bai viet / quang cao
 */
export interface IReportPostParams {
  pageIds: string[];
  businessId: string | number;
  startTime: number;
  endTime: number;
  type?: IReportPostType;
  timeFilter?: 1 | 2;
}

/**
 * IReportPostItem: Cau truc du lieu cua mot ban ghi bao cao bai viet / quang cao
 */
export interface IReportPostItem {
  key: string; // ID bai viet hoac quang cao
  postId: string; // ID bai viet, voi type=2 la postId goc cua quang cao
  conversation: number;
  new_conversation: number;
  old_conversation: number;
  order: number;
  amount: number;
  description: string;
  ratioOrderConversation: number;
  ratioAmountConversation: number;
}

export interface IReportPostTotal {
  conversation: number;
  new_conversation: number;
  old_conversation: number;
  order: number;
  amount: number;
  ratioOrderConversation?: number;
  ratioAmountConversation?: number;
}

export interface IReportPostData {
  items: IReportPostItem[];
  total: IReportPostTotal;
}

/**
 * IReportPostRow: Du lieu da format de render table tren FE
 */
export interface IReportPostRow extends IReportPostTotal {
  [key: string]: string | number | boolean | undefined;

  id?: string | number;
  key?: string;
  postId?: string;
  description?: string;
  total?: boolean;
  fwbold?: boolean;
}

export interface IReportLivestreamsParams {
  businessId: string | number;
  pageIds: string[];
  startTime: number;
  endTime: number;
}

export interface IReportLivestreamsTotal {
  customer: number;
  comment: number;
  all_order: number;
  order_draf: number;
  order_done: number;
  revenue: number;
  avgOrder: number;
}

export interface IReportLivestreamsItem {
  id: string;
  pageId: string;
  createdTime: number;
  customer: number;
  comment: number;
  all_order: number;
  done_order: number;
  amount: number;
  ratio_order_draf: number;
  ratio_order: number;
  avg_order: number;
  description?: string;
  picture?: string;
}

export interface IReportLivestreamsData {
  total: IReportLivestreamsTotal | [];
  items: IReportLivestreamsItem[];
}

export interface IReportLiveshoppingParams {
  businessId: string | number;
  startTime: number;
  endTime: number;
}

export interface IReportLiveshoppingItem {
  [key: string]: string | number | undefined;

  id: string;
  liveScriptId: string;
  displayName: string;
  countCommentUser: number;
  countCommentKeyword: number;
  countAddToCart: number;
  countOrder: number;
  totalRevenue: number;
  createdTime: number;
}

export interface IReportLiveshoppingData {
  table: {
    header: string[];
    items: IReportLiveshoppingItem[];
  };
}

export type IReportLiveshoppingSummary = Pick<IReportLiveshoppingItem,
  'countCommentUser' |
  'countCommentKeyword' |
  'countAddToCart' |
  'countOrder' |
  'totalRevenue'
>;

export interface IReportLivestreamDescriptionParams {
  id: string;
  pageId: string;
}

export interface IReportLivestreamDescriptionData {
  id: string;
  description?: string;
  picture?: string;
  thumbnails?: {
    data?: {
      uri: string;
      height: number;
      width: number;
    }[];
  };
}

export interface IReportLivestreamDetailParams {
  id: string;
  countLoad?: number;
}

export interface IReportLivestreamDetailData {
  pageId?: string;
  live: {
    view_count: number;
    times: {
      start?: number;
      end?: number;
      display?: string;
    };
    isLive: boolean;
    raitoView: number;
    thumb?: string;
  };
  comment: {
    total: number;
    customer: number;
    match_script: number;
    cr: number;
    count_mobile?: number;
  };
  order: {
    all: number;
    done: number;
    new_customer: number;
    old_customer: number;
  };
  revenue: {
    total: number;
    product: number;
    avgOrder: number;
    avgQuantity: number;
    total_quantity?: number;
  };
  target: {
    number: number;
    gmv: number;
  };
}

export interface IReportLivestreamMethodBuyParams {
  id: string;
  dir?: 'asc' | 'desc';
  field_order?: string;
}

export interface IReportLivestreamMethodBuyGroup {
  total_order: number;
  order_pending: number;
  order_done: number;
  revenue: number;
}

export interface IReportLivestreamMethodBuyData {
  live_shoping: IReportLivestreamMethodBuyGroup;
  live_script: IReportLivestreamMethodBuyGroup;
  total: {
    order: number;
    pending: number;
    done: number;
    revenue: number;
  };
}

export interface IReportLivestreamRevenueMethodParams {
  id: string;
  nextScript?: unknown[];
  nextShoping?: unknown[];
}

export interface IReportLivestreamRevenueMethodData {
  dataScript: number;
  dataShoping: number;
  total: number;
  dataScriptOrderDone: number;
  dataLiveShopingOrderDone: number;
  nextScript: unknown[];
  nextShoping: unknown[];
}

export interface IReportLivestreamRevenueParams {
  id: string;
  lastLimit?: number;
  direct?: 1 | 2;
  loadMore?: boolean | number;
}

export interface IReportLivestreamRevenueItem {
  time: {
    start: number;
    end: number;
    running_time: number;
    display: string;
    call: boolean;
  };
  data: {
    comment: number;
    amount: number;
    order: number;
  };
}

export interface IReportLivestreamProductItem {
  stt?: number | string;
  id?: string;
  name?: string;
  price?: number;
  total_order?: number;
  total_quantity?: number;
  total_price?: number;
  total_quantity_done?: number;
  total_price_done?: number;
  cr?: number;
  fwBold?: boolean;
}

export interface IReportLivestreamProductParams {
  id?: string;
  ids?: string[];
  dir?: 'asc' | 'desc';
  field_order?: string;
  export?: unknown;
}

export interface IReportLivestreamProductData {
  result: IReportLivestreamProductItem[];
}

export interface IReportStaffActivityParams {
  businessId: string | number;
  startTime: number;
  endTime: number;
}

export interface IReportStaffActivityTime {
  start: number;
  end: number;
}

export interface IReportStaffActivityItem {
  id: string;
  name: string;
  times: IReportStaffActivityTime[];
}

export interface IReportStaffActivityResponse {
  items: IReportStaffActivityItem[];
  next: boolean;
}

export interface IReportInteractiveDashboardParams {
    pageIds: string[];
    times: number[][];
}

export interface IReportInteractiveDashboardResponse {
    countTotalCustomer?: number;
    countTotalConversation?: number;
    countNewCustomer?: number;
    countOldCustomer?: number;
    newCustomerConversationInbox?: number;
    oldCustomerConversationInbox?: number;
    newCustomerConversationComment?: number;
    oldCustomerConversationComment?: number;
}

export interface IReportMsg6MonthParams {
    pageIds: string[];
    rangeTimes: number[][];
}

export interface IReportMsg6MonthResponse {
    pie: [];
    col: [];
}

export interface IReportInteractive30DayParams {
    pageIds: string[];
    startTime: number;
    endTime: number;
}

export interface IReportInteractive30DayResponse {
  header: string[];
  items: IReportInteractive30DayItem[];
}

export interface IReportInteractive30DayItem {
  displayName: string;
  total: number;
  days: number[];
}

export interface IReportSplitParams {
  businessId: string|number;
  pageIds: string[];
  fromTime: number;
  toTime: number;
}

export interface IReportSplitResponce {
  next: number[]|null;
  totalItems: number;
  result: IReportSplitItem[];
  newCusomerToltal: number;
  oldCusomerToltal: number;
  totalConverations: {
    assigneed: number;
    processed: number;
  }
}

export interface IReportSplitItem {
  user: {
    id: string;
    displayName: string;
    avatar: string;
    active: number;
    joined: number;
  };
  assigned: {
    total_splits: number;
    total_conversations: number;
    new_customer: {
      splits: number;
      conversations: number;
    };
    old_customer: {
      splits: number;
      conversations: number;
    };
    from_other: {
      splits: number;
      conversations: number;
    };
  };
  transferred: {
    total_splits: number;
    total_conversations: number;
    absent: {
      splits: number;
      conversations: number;
    };
    overtime: {
      splits: number;
      conversations: number;
    };
    limit: {
      splits: number;
      conversations: number;
    };
    manual: {
      splits: number;
      conversations: number;
    };
  }
}
export interface IReportMetaCapiStatusParams {
  pageIds: string[];
}
export interface IReportMetaCapiParams {
  pageIds: string[];
  startDate?: number;
  endDate?: number;
  size?: number;
  orderId?: string;
  next?: string;
  adId?: string;
  customerName?: string;
  status?: number;
  eventName?: string;
  sort?: string;
  format?: string;
  orderlist?: number;
  act?: string;
}

export interface IReportDashboardTotalParams {
  businessId: string;
  pageIds: string[];
  startTime: number;
  endTime: number;
}

export interface IReportFbAdResponce {
  account_name: string;
  account_id: string;
  account_currency: string;
  spend: string;
  reach: string;
  impressions: string;
  frequency: string;
  action_values: string[];
}

export interface IReportFbAdsByAdParams {
  businessId: string;
  adaccountId: string;
  startExpired: number;
  endExpired: number;
  campaignId?: string;
  after?: string;
}

export interface IReportFbAdsByAdResponce {
  campaign_id: string;
  campaign_name: string;
  adset_id: string;
  adset_name: string;
  ad_id: string;
  ad_name: string;
  spend: string;
  impressions: string;
  reach: string;
  frequency: string;
  clicks: string;
  conversions: string;
  cost_per_conversion: any[];
  purchase_roas: any[];
}

// ── Threads Report ──────────────────────────────────────────────────────────

export interface IReportThreadsParams {
  pageIds: string[];
  startTime: number;
  endTime: number;
  businessId: number;
}

export interface IReportThreadsItem {
  key?: string;
  date_string?: string;
  totalPosts: number;
  totalReplies: number;
  pendingReplies: number;
  approvedReplies: number;
  ignoredReplies: number;
  hiddenReplies: number;
  engagementRate?: number;
  // FE computed
  id?: string | number;
  total?: boolean;
  displayName?: string;
  fwbold?: boolean;
}

export interface ReportFmmFilterParams {
  fromAt: number;              // Timestamp bắt đầu (Epoch second)
  toAt: number;                // Timestamp kết thúc (Epoch second)
  mode?: 1 | 2;                // 1: Fanpage, 2: TKQC
  businessId?: number | string;
  pageId?: string;
  pageIds?: string[];
  adAccountId?: string;
  size?: number;
  after?: number;
  export?: number | boolean;
}

// Interface một dòng dữ liệu báo cáo
export interface ReportFmmItem {
  id: string;                  // ID định danh (Page ID hoặc Ad Account ID tùy mode)
  name: string;                // Tên hiển thị (Tên Page hoặc Tên Tài khoản QC)
  totalDelivered: number;      // Tổng số lượng tin nhắn FMM gửi thành công
  totalSpend: number;          // Tổng chi phí đã tiêu (VND)
  campaignCount: number;       // Số lượng chiến dịch thuộc nhóm này
  pageId?: string;             // Có khi mode = 1
  adAccountId?: string;        // Có khi mode = 2
}

// Interface dữ liệu phân trang trả về trong data
export interface ReportFmmResponseData {
  result: ReportFmmItem[];     // Danh sách các dòng báo cáo
  total: number;               // Tổng số bản ghi tìm thấy
  offset: number;              // Offset hiện tại
  next: number;                // Offset cho trang kế tiếp (bằng 0 nếu đã hết trang)
}

export interface ReportEventParams {
  startTime: number;
  endTime: number;
  viewType: 1|2 // 1: tổng, 2: chi tiết
  sort?: string // 'successDesc': sắp xếp theo event thành công, 'failDesc': sắp xếp theo event thất bại
}

export interface UserReportItem {
  key: string;            // Mã định danh chỉ số để map Transloco
  displayName: string;    // Tên hiển thị mặc định
  total: number;          // Tổng số lượng
  '1'?: number | string;  // Facebook
  '2'?: number | string;  // Instagram
  '4'?: number | string;  // Shopee
  '5'?: number | string;  // Zalo OA
  '6'?: number | string;  // Lazada
  '7'?: number | string;  // Telegram
  '8'?: number | string;  // WhatsApp
  '9'?: number | string;  // TikTok
  '10'?: number | string; // Zalo cá nhân
  '11'?: number | string; // TikTok Business
  '12'?: number | string; // Threads
  [channelId: string]: any;
}

export interface ReportConversationParams {
  pageId?: string;
  startTime: number;
  endTime: number;
  onlyBizAiPage: boolean;
}

export interface PageConversationItem {
  pageId: string;                     // ID Fanpage
  pageName: string;                   // Tên Fanpage
  newConversations: number;           // Số lượng hội thoại mới tạo
  interactedConversations: number;    // Số lượng hội thoại đã tương tác
  usedBizaiConversations: number;     // Số hội thoại đã sử dụng Business Agent (AI)
  humanHandledConversations: number;  // Số hội thoại do nhân viên (người) xử lý
  bizaiRate: number;                  // Tỷ lệ % sử dụng Business Agent (tính đến 2 chữ số thập phân)
}
export interface AffiliateCodeParams {
  affiliateCode?: string;
  startTime: number;
  endTime: number;
}
// Chi tiết mã giới thiệu và số lượng trong ngày
export interface AffiliateCodeCount {
  affiliateCode: string; // Mã giới thiệu
  count: number;         // Số lượng Fanpage được tạo
}

// Thống kê theo từng ngày
export interface ReferralCodeDayItem {
  date: string;                   // Ngày thống kê (định dạng YYYY-MM-DD)
  total: number;                  // Tổng số Fanpage tạo trong ngày
  codes: AffiliateCodeCount[];    // Danh sách mã giới thiệu và số lượng
}
